import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { ref, get, push, set as dbSet, remove } from 'firebase/database';
import MobileLayout from '../../components/mobile/MobileLayout';
import { apiFetch } from '../../utils/api';

/* ─── Types ─────────────────────────────────────────────────────── */
interface UserButton { title: string; payload: string; }
interface ListRow    { title: string; payload: string; }
interface ListSection { title: string; rows: ListRow[]; }

interface CampaignMessage {
  type: 'text' | 'image' | 'document' | 'buttons' | 'list';
  text: string;
  mediaUrl?: string;
  filename?: string;
  linkUrl?: string;
  userButtons?: UserButton[];
  listSections?: ListSection[];
}

interface CampaignSchedule { days: string[]; time: string; }

interface Campaign {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused';
  message: CampaignMessage;
  schedule: CampaignSchedule;
  stats: { sent: number; failed: number };
  createdAt: number;
  lastSentAt?: number;
  unsubscribed?: number;
  targetListId?: string;
}

interface BotEntry { id: string; businessName: string; botType: string; userId: string; }
interface ContactList { id: string; name: string; createdAt: number; }

/* ─── Constants ─────────────────────────────────────────────────── */
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS: Record<string,string> = {
  monday:'Mon',tuesday:'Tue',wednesday:'Wed',thursday:'Thu',friday:'Fri',saturday:'Sat',sunday:'Sun'
};
const PLAN_LIMITS: Record<string,{maxActive:number;maxDays:number}> = {
  free:       { maxActive: 0, maxDays: 0 },
  starter:    { maxActive: 1, maxDays: 1 },
  business:   { maxActive: 3, maxDays: 2 },
  agency:     { maxActive: 0, maxDays: 7 },
  enterprise: { maxActive: 0, maxDays: 7 },
};
const BLANK_MSG: CampaignMessage = {
  type:'text',text:'',mediaUrl:'',filename:'',linkUrl:'',
  userButtons:[{title:'',payload:''}],
  listSections:[{title:'Options',rows:[{title:'',payload:''}]}],
};
const BLANK_FORM = { name:'', status:'draft' as Campaign['status'], message:{...BLANK_MSG}, schedule:{days:[] as string[],time:'09:00'}, targetListId:undefined as string|undefined };

function pad(n:number){return String(n).padStart(2,'0');}
function fmtDate(ts?:number){
  if(!ts)return '—';
  const d=new Date(ts);
  return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ─── Status helpers ─────────────────────────────────────────────── */
function statusColor(s:string){
  if(s==='active')return '#10b981';
  if(s==='paused')return '#f59e0b';
  return '#64748b';
}

/* ─── Shared inline style atoms ─────────────────────────────────── */
const input: React.CSSProperties = {
  background:'var(--bg-input)',border:'1px solid var(--card-border)',borderRadius:8,
  padding:'10px 14px',color:'var(--text-primary)',fontSize:14,width:'100%',
  boxSizing:'border-box',outline:'none',fontFamily:'inherit',
};
const textarea: React.CSSProperties = {
  ...input,resize:'vertical',minHeight:90,
};
const primaryBtn: React.CSSProperties = {
  background:'#5B4FE8',color:'white',borderRadius:9,padding:'12px',
  fontSize:14,fontWeight:600,border:'none',width:'100%',cursor:'pointer',
  minHeight:44,
};
const ghostBtn: React.CSSProperties = {
  background:'var(--bg-element)',color:'var(--text-primary)',
  border:'1px solid var(--card-border)',borderRadius:9,padding:'12px',
  fontSize:14,fontWeight:600,cursor:'pointer',width:'100%',minHeight:44,
};
const card: React.CSSProperties = {
  borderRadius:12,background:'var(--bg-card)',border:'1px solid var(--card-border)',
  boxShadow:'var(--card-shadow)',marginBottom:12,padding:16,
};
const label: React.CSSProperties = {
  display:'block',fontSize:11,color:'var(--text-faint)',
  textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6,fontWeight:700,
};
function badge(color:string):React.CSSProperties{
  return {borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,
    background:color+'22',color,textTransform:'uppercase',letterSpacing:'0.04em'};
}
function chip(active:boolean):React.CSSProperties{
  return {
    padding:'6px 14px',borderRadius:20,border:`1px solid ${active?'#5B4FE8':'var(--card-border)'}`,
    background:active?'rgba(91,79,232,0.2)':'var(--bg-element)',
    color:active?'#a5b4fc':'var(--text-muted)',cursor:'pointer',fontSize:13,
    fontWeight:600,whiteSpace:'nowrap',flexShrink:0,minHeight:36,
  };
}

/* ════════════════════════════════════════════════════════════════ */
export default function CampaignsMobile() {
  const navigate = useNavigate();

  /* auth / bot list */
  const [uid, setUid]           = useState('');
  const [userPlan, setUserPlan] = useState('free');
  const [bots, setBots]         = useState<BotEntry[]>([]);
  const [selectedBotId, setSelectedBotId] = useState('');
  const [loading, setLoading]   = useState(true);

  /* campaigns */
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [lists, setLists]         = useState<ContactList[]>([]);
  const [campsLoading, setCampsLoading] = useState(false);

  /* views: 'list' | 'detail' | 'create' */
  const [view, setView]         = useState<'list'|'detail'|'create'>('list');
  const [detailCampaign, setDetailCampaign] = useState<Campaign|null>(null);

  /* create form */
  const [form, setForm]         = useState<typeof BLANK_FORM>({...BLANK_FORM,message:{...BLANK_MSG}});
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [createTab, setCreateTab] = useState<'message'|'schedule'>('message');

  /* toast */
  const [toast, setToast]       = useState('');
  const showToast = (msg:string)=>{ setToast(msg); setTimeout(()=>setToast(''),3000); };

  const limits = PLAN_LIMITS[userPlan]||PLAN_LIMITS.free;

  /* ── Auth + bots ── */
  useEffect(()=>{
    const unsub = auth.onAuthStateChanged(async user=>{
      if(!user){ navigate('/login'); return; }
      setUid(user.uid);
      try{
        const [userSnap,botsSnap] = await Promise.all([
          get(ref(db,`users/${user.uid}`)),
          get(ref(db,'bots')),
        ]);
        const userData = userSnap.val()||{};
        setUserPlan(userData.plan||'free');
        const allBots: BotEntry[] = [];
        botsSnap.forEach(c=>{
          const b = c.val();
          if(b.userId===user.uid && b.botType==='whatsapp'){
            allBots.push({id:c.key!,...b});
          }
        });
        setBots(allBots);
        if(allBots.length>0) setSelectedBotId(allBots[0].id);
      }catch(e){ console.error(e); }
      finally{ setLoading(false); }
    });
    return unsub;
  },[navigate]);

  /* ── Load campaigns when bot changes ── */
  const loadCampaigns = useCallback(async(botId:string)=>{
    if(!botId) return;
    setCampsLoading(true);
    try{
      const res  = await apiFetch(`/api/campaigns/${botId}`);
      const data = await res.json();
      if(data.success) setCampaigns(data.campaigns||[]);
    }catch(e){ console.error(e); }
    finally{ setCampsLoading(false); }
  },[]);

  const loadLists = useCallback(async(botId:string)=>{
    if(!botId) return;
    const snap = await get(ref(db,`campaignLists/${botId}`));
    const d = snap.val()||{};
    const arr:ContactList[] = Object.entries(d).map(([id,v]:any)=>({id,...v}));
    setLists(arr.sort((a,b)=>b.createdAt-a.createdAt));
  },[]);

  useEffect(()=>{
    if(selectedBotId){ loadCampaigns(selectedBotId); loadLists(selectedBotId); }
  },[selectedBotId,loadCampaigns,loadLists]);

  /* ── Stats ── */
  const stats = useMemo(()=>({
    total:    campaigns.length,
    active:   campaigns.filter(c=>c.status==='active').length,
    paused:   campaigns.filter(c=>c.status==='paused').length,
    draft:    campaigns.filter(c=>c.status==='draft').length,
  }),[campaigns]);

  /* ── Create form helpers ── */
  const setMsg = (patch:Partial<CampaignMessage>)=>
    setForm(f=>({...f,message:{...f.message,...patch}}));

  const toggleDay=(day:string)=>{
    const days = form.schedule.days;
    if(days.includes(day)){
      setForm(f=>({...f,schedule:{...f.schedule,days:days.filter(d=>d!==day)}}));
    } else {
      if(limits.maxDays>0 && days.length>=limits.maxDays) return;
      setForm(f=>({...f,schedule:{...f.schedule,days:[...days,day]}}));
    }
  };

  const openCreate=()=>{
    setForm({...BLANK_FORM,message:{...BLANK_MSG}});
    setFormError('');
    setCreateTab('message');
    setView('create');
  };

  const saveCampaign=async()=>{
    if(!form.name.trim()){ setFormError('Campaign name is required'); return; }
    if(!form.message.text.trim()){ setFormError('Message body is required'); return; }
    setSaving(true); setFormError('');
    try{
      const payload={...form,targetListId:form.targetListId||null};
      const res = await apiFetch(`/api/campaigns/${selectedBotId}`,{
        method:'POST',body:JSON.stringify({campaign:payload}),
      });
      const data = await res.json();
      if(!data.success){ setFormError(data.error||'Failed to create'); return; }
      await loadCampaigns(selectedBotId);
      showToast('Campaign created');
      setView('list');
    }catch(err:any){ setFormError(err.message||'Something went wrong'); }
    finally{ setSaving(false); }
  };

  /* ── Delete ── */
  const deleteCampaign=async(c:Campaign)=>{
    if(!window.confirm(`Delete "${c.name}"?`)) return;
    await apiFetch(`/api/campaigns/${selectedBotId}/${c.id}`,{method:'DELETE'});
    showToast('Deleted');
    setView('list');
    setDetailCampaign(null);
    await loadCampaigns(selectedBotId);
  };

  /* ─── RENDER helpers ──────────────────────────────────────────── */

  if(loading) return(
    <MobileLayout title="Campaigns">
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',color:'var(--text-muted)',fontSize:14}}>
        Loading...
      </div>
    </MobileLayout>
  );

  if(userPlan==='free') return(
    <MobileLayout title="Campaigns">
      <div style={{padding:24,textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:16}}>📢</div>
        <div style={{fontWeight:800,fontSize:20,marginBottom:8}}>Campaigns require a paid plan</div>
        <div style={{color:'var(--text-muted)',fontSize:14,marginBottom:24,lineHeight:1.6}}>
          Upgrade to Starter or higher to send mass messages to your WhatsApp contacts.
        </div>
        <button onClick={()=>navigate('/billing')} style={primaryBtn}>View plans</button>
      </div>
    </MobileLayout>
  );

  if(bots.length===0) return(
    <MobileLayout title="Campaigns">
      <div style={{padding:24,textAlign:'center',color:'var(--text-muted)',fontSize:14}}>
        No WhatsApp bots found. Create one from the dashboard first.
      </div>
    </MobileLayout>
  );

  /* ── DETAIL VIEW ── */
  if(view==='detail' && detailCampaign){
    const c = detailCampaign;
    return(
      <MobileLayout
        title={c.name||'Campaign'}
        onBack={()=>{ setView('list'); setDetailCampaign(null); }}
      >
        <div style={{padding:16}}>
          {/* Status + name */}
          <div style={{...card}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
              <span style={badge(statusColor(c.status))}>{c.status}</span>
            </div>
            <div style={{fontWeight:700,fontSize:18,marginBottom:4}}>{c.name}</div>
            <div style={{fontSize:13,color:'var(--text-muted)'}}>
              Created {fmtDate(c.createdAt)}
            </div>
          </div>

          {/* Message */}
          <div style={{...card}}>
            <div style={label}>Message</div>
            <div style={{fontSize:14,lineHeight:1.6,color:'var(--text-primary)',whiteSpace:'pre-wrap'}}>
              {c.message?.text||'(no message body)'}
            </div>
            {c.message?.type!=='text' && (
              <div style={{marginTop:8}}>
                <span style={{fontSize:11,background:'var(--bg-element)',color:'var(--text-muted)',padding:'2px 8px',borderRadius:6,fontWeight:600}}>
                  {c.message.type.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div style={{...card}}>
            <div style={label}>Stats</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
              {[
                {label:'Sent',value:c.stats?.sent||0,color:'#10b981'},
                {label:'Failed',value:c.stats?.failed||0,color:'#ef4444'},
                {label:'Unsub',value:c.unsubscribed||0,color:'#f59e0b'},
              ].map(s=>(
                <div key={s.label} style={{background:'var(--bg-element)',borderRadius:10,padding:'12px 8px',textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:800,color:s.color}}>{s.value}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2,fontWeight:600}}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div style={{...card}}>
            <div style={label}>Schedule</div>
            {(c.schedule?.days?.length||0)>0 ? (
              <div style={{fontSize:14}}>
                {c.schedule.days.map(d=>DAY_LABELS[d]).join(', ')} at {c.schedule.time}
              </div>
            ):(
              <div style={{fontSize:14,color:'var(--text-muted)'}}>Not scheduled</div>
            )}
            <div style={{marginTop:8,fontSize:13,color:'var(--text-muted)'}}>Last sent: {fmtDate(c.lastSentAt)}</div>
          </div>

          {/* Target */}
          {c.targetListId && (
            <div style={{...card}}>
              <div style={label}>Target</div>
              <div style={{fontSize:14}}>Custom contact list</div>
            </div>
          )}

          {/* Delete */}
          <button
            onClick={()=>deleteCampaign(c)}
            style={{...primaryBtn,background:'#ef4444',marginTop:8}}
          >
            Delete Campaign
          </button>
        </div>
      </MobileLayout>
    );
  }

  /* ── CREATE VIEW ── */
  if(view==='create'){
    return(
      <MobileLayout
        title="New Campaign"
        onBack={()=>setView('list')}
        hideNav
      >
        <div style={{padding:16}}>
          {/* Tabs */}
          <div style={{display:'flex',gap:6,marginBottom:16,background:'var(--bg-element)',borderRadius:10,padding:4}}>
            {(['message','schedule'] as const).map(t=>(
              <button key={t} onClick={()=>setCreateTab(t)} style={{
                flex:1,padding:'8px 0',borderRadius:8,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,
                background:createTab===t?'#5B4FE8':'transparent',
                color:createTab===t?'white':'var(--text-muted)',
              }}>
                {t==='message'?'Message':'Schedule'}
              </button>
            ))}
          </div>

          {formError && (
            <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',color:'#ef4444',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:14}}>
              {formError}
            </div>
          )}

          {createTab==='message' && (
            <>
              {/* Name */}
              <div style={{marginBottom:16}}>
                <div style={label}>Campaign Name</div>
                <input
                  value={form.name}
                  onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                  placeholder="e.g. Weekend Promo"
                  style={input}
                />
              </div>

              {/* Status */}
              <div style={{marginBottom:16}}>
                <div style={label}>Status</div>
                <select
                  value={form.status}
                  onChange={e=>setForm(f=>({...f,status:e.target.value as Campaign['status']}))}
                  style={{...input,appearance:'none'}}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>

              {/* Message type */}
              <div style={{marginBottom:16}}>
                <div style={label}>Message Type</div>
                <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:4}}>
                  {(['text','image','document','buttons','list'] as const).map(t=>(
                    <button key={t} onClick={()=>setMsg({type:t})} style={chip(form.message.type===t)}>
                      {t==='text'?'💬 Text':t==='image'?'🖼️ Image':t==='document'?'📄 Doc':t==='buttons'?'🔘 Buttons':'📋 List'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target */}
              <div style={{marginBottom:16}}>
                <div style={label}>Target Audience</div>
                <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:4}}>
                  <button onClick={()=>setForm(f=>({...f,targetListId:undefined}))} style={chip(!form.targetListId)}>
                    👥 All contacts
                  </button>
                  {lists.map(l=>(
                    <button key={l.id} onClick={()=>setForm(f=>({...f,targetListId:l.id}))} style={chip(form.targetListId===l.id)}>
                      📋 {l.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message body */}
              <div style={{marginBottom:16}}>
                <div style={label}>Message Body</div>
                <textarea
                  value={form.message.text}
                  onChange={e=>setMsg({text:e.target.value})}
                  placeholder={form.message.type==='image'?'Caption for the image…':form.message.type==='document'?'Description for the document…':'Write your campaign message…'}
                  style={textarea}
                />
                <div style={{fontSize:11,color:'var(--text-faint)',marginTop:4}}>{form.message.text.length} characters</div>
              </div>

              {/* Media URL */}
              {(form.message.type==='image'||form.message.type==='document') && (
                <div style={{marginBottom:16}}>
                  <div style={label}>{form.message.type==='image'?'Image URL':'Document URL (PDF)'}</div>
                  <input value={form.message.mediaUrl||''} onChange={e=>setMsg({mediaUrl:e.target.value})} placeholder="https://..." style={input}/>
                  {form.message.type==='document' && (
                    <div style={{marginTop:10}}>
                      <div style={label}>Filename</div>
                      <input value={form.message.filename||''} onChange={e=>setMsg({filename:e.target.value})} placeholder="brochure.pdf" style={input}/>
                    </div>
                  )}
                </div>
              )}

              {/* Link URL */}
              {form.message.type==='text' && (
                <div style={{marginBottom:16}}>
                  <div style={label}>Link URL (optional)</div>
                  <input value={form.message.linkUrl||''} onChange={e=>setMsg({linkUrl:e.target.value})} placeholder="https://..." style={input}/>
                </div>
              )}

              {/* Buttons config */}
              {form.message.type==='buttons' && (
                <div style={{marginBottom:16}}>
                  <div style={label}>Buttons (max 2)</div>
                  {(form.message.userButtons||[]).map((btn,i)=>(
                    <div key={i} style={{marginBottom:10,background:'var(--bg-element)',borderRadius:10,padding:12}}>
                      <div style={{fontSize:12,color:'var(--text-muted)',fontWeight:600,marginBottom:8}}>Button {i+1}</div>
                      <input value={btn.title} onChange={e=>{const ub=[...(form.message.userButtons||[])];ub[i]={...ub[i],title:e.target.value};setMsg({userButtons:ub});}} placeholder="Button label" style={{...input,marginBottom:8}}/>
                      <input value={btn.payload} onChange={e=>{const ub=[...(form.message.userButtons||[])];ub[i]={...ub[i],payload:e.target.value};setMsg({userButtons:ub});}} placeholder="Trigger keyword" style={input}/>
                    </div>
                  ))}
                  {(form.message.userButtons||[]).length<2 && (
                    <button onClick={()=>setMsg({userButtons:[...(form.message.userButtons||[]),{title:'',payload:''}]})} style={{...ghostBtn,width:'auto',padding:'8px 16px',fontSize:13}}>
                      + Add button
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {createTab==='schedule' && (
            <>
              <div style={{marginBottom:16}}>
                <div style={label}>Send days</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {DAYS.map(day=>{
                    const active=form.schedule.days.includes(day);
                    const disabled=!active&&limits.maxDays>0&&form.schedule.days.length>=limits.maxDays;
                    return(
                      <button key={day} onClick={()=>toggleDay(day)} disabled={disabled} style={{
                        padding:'8px 14px',borderRadius:8,border:`1px solid ${active?'#5B4FE8':'var(--card-border)'}`,
                        background:active?'rgba(91,79,232,0.2)':'var(--bg-element)',
                        color:disabled?'var(--text-faint)':active?'#a5b4fc':'var(--text-muted)',
                        cursor:disabled?'not-allowed':'pointer',fontWeight:600,fontSize:13,minHeight:40,
                      }}>
                        {DAY_LABELS[day]}
                      </button>
                    );
                  })}
                </div>
                {limits.maxDays>0 && (
                  <div style={{marginTop:8,fontSize:12,color:'var(--text-muted)'}}>
                    {limits.maxDays - form.schedule.days.length} day slot(s) remaining on your plan
                  </div>
                )}
              </div>

              <div style={{marginBottom:16}}>
                <div style={label}>Send time (Africa/Harare, 24hr)</div>
                <input
                  type="time"
                  value={form.schedule.time}
                  onChange={e=>setForm(f=>({...f,schedule:{...f.schedule,time:e.target.value}}))}
                  style={{...input,colorScheme:'dark'}}
                />
              </div>

              <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:10,padding:'12px 14px',fontSize:13,color:'var(--text-secondary)',lineHeight:1.6}}>
                The scheduler runs every 5 minutes. Set status to <strong>Active</strong> for automatic sends.
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{marginTop:20,display:'flex',flexDirection:'column',gap:10}}>
            <button onClick={saveCampaign} disabled={saving} style={{...primaryBtn,opacity:saving?0.6:1}}>
              {saving?'Creating...':'Create Campaign'}
            </button>
            <button onClick={()=>setView('list')} style={ghostBtn}>Cancel</button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  /* ── LIST VIEW (default) ── */
  const activeCampaigns = campaigns.filter(c=>c.status==='active').length;
  const canCreate = limits.maxActive===0 || activeCampaigns<limits.maxActive;

  return(
    <MobileLayout
      title="Campaigns"
      headerRight={
        canCreate ? (
          <button onClick={openCreate} style={{background:'#5B4FE8',color:'white',border:'none',borderRadius:9,padding:'6px 14px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
            + New
          </button>
        ) : undefined
      }
    >
      {/* Toast */}
      {toast && (
        <div style={{position:'fixed',bottom:80,left:16,right:16,background:'#10b981',color:'white',padding:'12px 16px',borderRadius:12,fontWeight:600,fontSize:14,zIndex:999,textAlign:'center'}}>
          {toast}
        </div>
      )}

      <div style={{padding:16}}>
        {/* Bot selector */}
        {bots.length>1 && (
          <div style={{marginBottom:16}}>
            <div style={label}>Bot</div>
            <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:4}}>
              {bots.map(b=>(
                <button key={b.id} onClick={()=>setSelectedBotId(b.id)} style={chip(selectedBotId===b.id)}>
                  💬 {b.businessName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats bar */}
        <div style={{display:'flex',gap:8,marginBottom:16,overflowX:'auto',paddingBottom:4}}>
          {[
            {label:'Total',value:stats.total,color:'#5B4FE8'},
            {label:'Active',value:stats.active,color:'#10b981'},
            {label:'Paused',value:stats.paused,color:'#f59e0b'},
            {label:'Draft',value:stats.draft,color:'#64748b'},
          ].map(s=>(
            <div key={s.label} style={{flexShrink:0,background:'var(--bg-card)',border:'1px solid var(--card-border)',borderRadius:10,padding:'8px 14px',textAlign:'center',minWidth:64}}>
              <div style={{fontSize:20,fontWeight:800,color:s.color}}>{s.value}</div>
              <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:600}}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Campaign list */}
        {campsLoading && (
          <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:14,padding:'32px 0'}}>Loading campaigns...</div>
        )}

        {!campsLoading && campaigns.length===0 && (
          <div style={{textAlign:'center',padding:'48px 16px'}}>
            <div style={{fontSize:48,marginBottom:12}}>📢</div>
            <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>No campaigns yet</div>
            <div style={{color:'var(--text-muted)',fontSize:14,marginBottom:24,lineHeight:1.6}}>
              Create your first campaign to start sending bulk WhatsApp messages to your contacts.
            </div>
            {canCreate && (
              <button onClick={openCreate} style={primaryBtn}>+ Create First Campaign</button>
            )}
          </div>
        )}

        {!campsLoading && campaigns.map(c=>(
          <div key={c.id} onClick={()=>{ setDetailCampaign(c); setView('detail'); }} style={{...card,cursor:'pointer'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:15,flex:1,marginRight:10}}>{c.name||'Untitled'}</div>
              <span style={badge(statusColor(c.status))}>{c.status}</span>
            </div>
            <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:12,color:'var(--text-muted)'}}>
              <span>📤 {c.stats?.sent||0} sent</span>
              {(c.schedule?.days?.length||0)>0 && (
                <span>📅 {c.schedule.days.map(d=>DAY_LABELS[d]).join(', ')}</span>
              )}
              {c.targetListId && <span>📋 Custom list</span>}
              {c.lastSentAt && <span>Last: {fmtDate(c.lastSentAt)}</span>}
            </div>
          </div>
        ))}

        {/* Upgrade nudge for free plan */}
        {userPlan!=='free' && !canCreate && (
          <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:12,padding:16,marginTop:8,textAlign:'center'}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Campaign limit reached</div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:12}}>Upgrade your plan for more active campaigns.</div>
            <button onClick={()=>navigate('/billing')} style={{...primaryBtn,width:'auto',padding:'8px 20px',fontSize:13}}>Upgrade plan</button>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}
